<template>
  <div class="musubi-page musubi-page--techalerts">
    <header class="musubi-page__head">
      <h1>Tech-stack alerts <span class="plan-pill">Enterprise</span></h1>
      <p class="text-muted">
        Changes on the websites of companies you're already working — a prospect's site going down, an SSL
        certificate breaking, a platform rebuild, a version that's reached end-of-life. These are timely
        reasons to reach out to a lead you already have, while the change is fresh.
      </p>
    </header>

    <!-- ── Gated teaser (non-Enterprise): violet plan-gate. Skeleton rows (never
         fabricated data) blur behind the upgrade panel. `gateLocked` is null while
         /me/usage loads (render nothing yet). ── -->
    <div v-if="gateLocked" class="ta-teaser">
      <div class="ta-teaser__blur" aria-hidden="true">
        <div class="ta-skel-tbl">
          <div v-for="n in 4" :key="n" class="ta-skel-row">
            <span class="skel" style="width:30%"></span>
            <span class="skel" style="width:12%"></span>
            <span class="skel" style="width:26%"></span>
            <span class="skel" style="width:10%"></span>
          </div>
        </div>
      </div>
      <div class="ta-teaser__panel">
        <div class="upgrade">
          <div class="upgrade__ic"><CygnusIcon name="bolt" /></div>
          <div class="upgrade__eyebrow">Available on Enterprise</div>
          <h3>Know the moment a prospect's site changes</h3>
          <p>
            Tech-stack alerts watch the websites of the companies already in your pipeline, and flag you the
            instant something changes. It's available on the <strong>Enterprise</strong> plan.
          </p>
          <div class="upgrade__benefits">
            <div class="benefit"><CygnusIcon name="check" /> Know when a prospect's site breaks — SSL expires or the site goes down — so you can reach out with a genuinely useful heads-up</div>
            <div class="benefit"><CygnusIcon name="check" /> Platform-change signals — a rebuild or version bump — that tell you who's investing in their site right now</div>
            <div class="benefit"><CygnusIcon name="check" /> Act while it's fresh — jump straight into a conversation with the change already in context</div>
          </div>
          <div class="upgrade__actions">
            <router-link to="/admin/usage-plans" class="btn ta-btn-violet">Upgrade to Enterprise</router-link>
            <router-link to="/admin/usage-plans" class="ta-compare">Compare plans <CygnusIcon name="arrow-right" /></router-link>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Entitled page ── -->
    <template v-else-if="gateLocked === false">
      <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

      <div class="coverage-note">
        <CygnusIcon name="info-circle" />
        <span>
          We only watch the sites of companies you hold an <strong>active prospect</strong> on — this isn't
          open-market discovery, it's a change-watch on your own pipeline. We re-check each site roughly
          <strong>every 90 days</strong>, and more often once a site looks unstable, so alerts arrive in waves
          rather than a steady trickle.
        </span>
      </div>

      <section class="ta-kpis">
        <MusubiKpiCard label="Changes · 7d" :value="kpis.changes_7d" delta="across your prospects" icon="code" />
        <!-- Urgent — red-tinted when broken sites exist (mockup severity accent);
             the shared KPI card has no danger variant, so this one is inline. -->
        <div class="m-kpi" :class="{ urgent: kpis.urgent > 0 }">
          <div class="m-kpi__label"><CygnusIcon name="warning" class="m-kpi__icon" :class="{ danger: kpis.urgent > 0 }" /><span>Urgent</span></div>
          <div class="m-kpi__value" :class="{ danger: kpis.urgent > 0 }">{{ kpis.urgent }}</div>
          <div class="m-kpi__delta" :class="{ danger: kpis.urgent > 0 }">{{ kpis.urgent ? 'site down + SSL broken' : 'no broken sites' }}</div>
        </div>
        <MusubiKpiCard label="Actioned" :value="kpis.actioned" icon="check-circle" />
        <MusubiKpiCard label="Prospects monitored" :value="kpis.prospects_monitored" delta="sites under change-watch" delta-dir="up" icon="check-circle" />
      </section>

      <!-- Urgent-attention banner — pins above the table when danger-type signals
           sit in the New tab. Names the exact prospects; disappears when none. -->
      <div v-if="urgentBanner.show" class="urgent-banner">
        <span class="urgent-banner__ic"><CygnusIcon name="warning" /></span>
        <div class="urgent-banner__body">
          <div class="urgent-banner__t">
            {{ urgentBanner.count }}
            {{ urgentBanner.count === 1 ? "of your prospects' websites is" : "of your prospects' websites are" }}
            actively broken right now
          </div>
          <div class="urgent-banner__d">
            {{ urgentBanner.text }} These are the warmest calls you'll make today — a genuinely helpful heads-up
            they'll be glad to get, and a natural way into the work.
          </div>
        </div>
        <div class="urgent-banner__cta">
          <button class="btn ta-btn-danger btn-sm" @click="showUrgentOnly">
            Show the {{ urgentBanner.count }} urgent
          </button>
        </div>
      </div>

      <div class="ta-tabs">
        <button v-for="t in tabs" :key="t.key" class="ta-tab" :class="{ active: tab === t.key }" @click="setTab(t.key)">
          {{ t.label }}<span v-if="counts[t.countKey]" class="count">{{ counts[t.countKey] }}</span>
        </button>
      </div>

      <div class="ta-toolbar">
        <select v-model="filters.event_type" class="ta-select" @change="load">
          <option value="">Any event type</option>
          <option value="ssl_broken">SSL broken</option>
          <option value="site_down">Site down</option>
          <option value="eol_reached">End-of-life reached</option>
          <option value="framework_change">Platform change</option>
          <option value="version_change">Version change</option>
          <option value="ssl_fixed">SSL fixed</option>
        </select>
        <select v-model="urgency" class="ta-select">
          <option value="">Any urgency</option>
          <option value="urgent">Urgent only (broken sites)</option>
          <option value="worth">Worth a look</option>
          <option value="positive">Positive signals</option>
        </select>
        <input v-model="filters.search" class="ta-input" placeholder="Search prospect or website…" style="width:210px" @keyup.enter="load" @blur="load">
      </div>

      <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

      <div v-if="loading" class="ta-loading">Loading tech-stack alerts…</div>

      <!-- Empty state — monitoring live, nothing changed -->
      <div v-else-if="!visibleRows.length" class="dc-empty teal-edge">
        <div class="dc-empty__icon"><CygnusIcon name="check-circle" /></div>
        <div class="dc-empty__title">
          {{ rows.length ? 'No alerts match these filters'
             : ('No changes across your ' + kpis.prospects_monitored + ' monitored prospects yet') }}
        </div>
        <div class="dc-empty__hint">
          Everything's being watched and nothing's moved — that's the normal state most of the time. We
          re-check each prospect's site roughly <strong>every 90 days</strong> (and more often once one looks
          unstable), so alerts arrive in <strong>waves</strong> rather than a steady trickle. There's nothing
          to sit and watch here.
        </div>
        <div class="dc-empty__why">
          <div class="row"><CygnusIcon name="bell" /> You'll get a bell digest the moment something changes — no need to check back.</div>
          <div class="row"><CygnusIcon name="search" /> Coverage tracks your pipeline: <strong>add more prospects and more sites come under watch</strong>, widening the odds of catching a timely change.</div>
          <div class="row"><CygnusIcon name="clock" /> Since checks are spaced ~90 days apart, a quiet month is expected — it doesn't mean nothing's happening on those sites, just that we haven't re-checked them yet.</div>
        </div>
        <div class="dc-empty__actions">
          <router-link to="/acquire/prospects" class="btn ta-btn-outline"><CygnusIcon name="plus" /> Add prospects</router-link>
        </div>
      </div>

      <!-- Table -->
      <div v-else class="ta-tbl">
        <table>
          <thead>
            <tr>
              <th style="width:30px"></th>
              <th>Prospect</th>
              <th>Alert</th>
              <th>What changed</th>
              <th>Occurred</th>
              <th style="text-align:right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in visibleRows" :key="r.signal_id">
              <tr :class="{ 'is-open': expanded === r.signal_id, 'danger-row': r.urgent }">
                <td><button class="chev" :class="{ open: expanded === r.signal_id }" @click="toggleExpand(r.signal_id)"><CygnusIcon name="chevron-right" /></button></td>
                <td>
                  <div class="co-name">{{ r.name }}</div>
                  <div v-if="r.website" class="co-web"><CygnusIcon name="globe" />{{ prettyHost(r.website) }}</div>
                </td>
                <td>
                  <span class="alert-badge" :class="badgeClass(r.event_type)"><span class="dot"></span>{{ badgeLabel(r.event_type) }}</span>
                </td>
                <td>
                  <div class="change">
                    <template v-if="r.old_value">
                      <span class="old">{{ oldLabel(r) }}</span>
                      <span class="ar">→</span>
                    </template>
                    <span class="new" :class="newTone(r.event_type)">{{ newLabel(r) }}</span>
                  </div>
                  <div class="means"><CygnusIcon name="info-circle" />{{ meansText(r.event_type) }}</div>
                </td>
                <td><span class="age" :class="{ fresh: isFresh(r) }"><CygnusIcon :name="isFresh(r) ? 'bolt' : 'clock'" />{{ ageLabel(r.occurred_at) }}</span></td>
                <td>
                  <div class="row-actions">
                    <button v-if="r.prospect_id" class="btn ta-btn-secondary btn-sm" @click="viewProspect(r)">View prospect</button>
                    <button v-if="r.status !== 'dismissed'" class="btn ta-btn-primary btn-sm" :disabled="busy" @click="startConversation(r)">
                      <CygnusIcon name="note-sticky" /> Start conversation
                    </button>
                    <button v-if="r.status !== 'dismissed'" class="btn-ghost" :disabled="busy" title="Dismiss" @click="dismiss(r)">
                      <CygnusIcon name="x" />
                    </button>
                    <span v-else class="ta-dismissed"><CygnusIcon name="check" /> Dismissed</span>
                  </div>
                </td>
              </tr>

              <!-- Drill-down -->
              <tr v-if="expanded === r.signal_id" class="drill">
                <td colspan="6" style="padding:0">
                  <div v-if="detailLoading" class="drill__loading">Loading change history…</div>
                  <div v-else class="drill__inner">
                    <div>
                      <h4>Change history<span v-if="r.website"> · {{ prettyHost(r.website) }}</span></h4>
                      <div v-if="detailHistory.length" class="timeline2">
                        <div v-for="(h, i) in detailHistory" :key="i" class="tl2" :class="{ now: h.is_current }">
                          <div class="tl2__t">
                            {{ historyTitle(h) }}
                            <span v-if="h.is_current" class="mini">Now</span>
                          </div>
                          <div class="tl2__m">{{ fmtDateTime(h.occurred_at) }} · {{ historySub(h) }}</div>
                        </div>
                      </div>
                      <div v-else class="text-muted small">No prior change history on this site.</div>

                      <h4>Current stack snapshot</h4>
                      <div class="stack">
                        <div v-if="detailStack.website_framework" class="stack-chip"><span class="k">Framework</span>{{ detailStack.website_framework }}</div>
                        <div v-if="detailStack.website_version" class="stack-chip"><span class="k">Version</span>{{ detailStack.website_version }}</div>
                        <div class="stack-chip" :class="sslChipClass(detailStack.ssl_status)"><span class="k">SSL</span>{{ sslLabel(detailStack.ssl_status) }}</div>
                        <div class="stack-chip" :class="reachChipClass(detailStack.business_status)"><span class="k">Reachable</span>{{ reachLabel(detailStack.business_status) }}</div>
                        <div v-if="detailStack.website_eol" class="stack-chip" :class="detailStack.website_eol === 'eol' ? 'warn' : ''"><span class="k">Support</span>{{ eolLabel(detailStack.website_eol) }}</div>
                        <div v-if="detailStack.region" class="stack-chip"><span class="k">Region</span>{{ detailStack.region }}</div>
                      </div>
                    </div>
                    <div>
                      <h4>Linked prospect</h4>
                      <div v-if="detailProspect" class="prospect">
                        <div class="prospect__top">
                          <span class="prospect__name">{{ detailProspect.name }}</span>
                          <router-link :to="'/acquire/prospects/' + detailProspect.prospect_id" class="prospect__link">
                            Open prospect <CygnusIcon name="arrow-right" />
                          </router-link>
                        </div>
                        <div class="prospect__meta">
                          <span class="stage-chip"><CygnusIcon name="diagram" />Stage: {{ prettyStage(detailProspect.stage) }}</span>
                          <span class="temp" :class="tempClass(detailProspect.temperature)"><span class="dot"></span>{{ prettyTemp(detailProspect.temperature) }}</span>
                        </div>
                        <div class="prospect__last"><CygnusIcon name="clock" />Last activity: {{ relTime(detailProspect.last_activity) }}</div>
                      </div>
                      <div v-else class="text-muted small">
                        No active prospect is linked to this signal any more (it may have been rejected since
                        the alert was raised).
                      </div>

                      <!-- AI-drafted opener (pool item). AI spend is a CLICK, not a
                           render: the draft is only fetched when the user asks for it.
                           Nothing sends — "Use this draft" opens the editable compose
                           modal prefilled with the subject/body. -->
                      <div v-if="detailProspect" class="ai-opener-wrap">
                        <button v-if="!openerDraft && !openerQuota" class="btn ta-ai-trigger" :disabled="openerBusy" @click="draftOpener(r)">
                          <CygnusIcon name="spinner" :spin="true" v-if="openerBusy" />
                          <CygnusIcon name="bolt" v-else />
                          {{ openerBusy ? "Drafting…" : "Draft an opener" }}
                        </button>

                        <div v-if="openerError" class="ai-opener-err"><CygnusIcon name="warning" /> {{ openerError }}</div>

                        <div v-if="openerQuota" class="ai-opener-quota">
                          <CygnusIcon name="pause" />
                          <span>You're out of AI tokens this period, so no opener was drafted — nothing was charged.
                            <router-link to="/admin/usage-plans">Top up</router-link> to draft openers again.</span>
                        </div>

                        <div v-if="openerDraft" class="ai-opener">
                          <div class="ai-opener__head">
                            <CygnusIcon name="bolt" /> Suggested opener
                            <span class="ai-opener__badge">AI-drafted</span>
                          </div>
                          <div class="ai-opener__body">A starting point built from this change + the prospect's context:</div>
                          <div class="ai-opener__quote">
                            <div v-if="openerDraft.subject" class="ai-opener__subject">{{ openerDraft.subject }}</div>
                            <div class="ai-opener__text">{{ openerDraft.body }}</div>
                          </div>
                          <div class="ai-opener__note">
                            <CygnusIcon name="info-circle" />
                            <span>AI-suggested copy from the change + prospect context. Read it, make it yours, and
                              check the facts before sending — nothing goes out on its own.</span>
                          </div>
                          <div class="ai-opener__cta">
                            <button class="btn ta-btn-primary btn-sm" @click="useDraft">
                              <CygnusIcon name="envelope" /> Use this draft
                            </button>
                            <button class="btn ta-btn-outline btn-sm" :disabled="openerBusy" @click="draftOpener(r)">
                              <CygnusIcon name="refresh" /> Redraft
                            </button>
                          </div>
                        </div>
                      </div>

                      <div class="drill__cta">
                        <button v-if="r.status !== 'dismissed'" class="btn ta-btn-primary" :disabled="busy" @click="startConversation(r)">
                          <CygnusIcon name="note-sticky" /> Start conversation
                        </button>
                        <button v-if="detailProspect" class="btn ta-btn-outline" @click="viewProspect(r)">View prospect</button>
                        <button v-if="r.status !== 'dismissed'" class="btn btn-ghost btn-sm" :disabled="busy" @click="dismiss(r)">Dismiss</button>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Embedded compose modal for "Use this draft" — the same modal the prospect
         page uses, prefilled with the (editable) AI opener. Nothing sends on its own. -->
    <MusubiComposeModal :open="composeOpen" :context="composeContext" @close="composeOpen = false" @sent="onComposed" />
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiKpiCard from "@root/components/ui/MusubiKpiCard.vue";
import MusubiComposeModal from "@root/components/MusubiComposeModal.vue";

/**
 * AdminTechAlerts — the tenant-facing tech-stack change-alerts page. Mirrors
 * AdminCompaniesHouse.vue: a signals list with a New / Actioned / Dismissed
 * lifecycle, an expandable drill-down (change history + stack snapshot + linked
 * prospect), and commit actions.
 *
 * The severity ladder maps event_type → status colour: ssl_broken + site_down =
 * danger (with a red row-edge + urgent KPI + banner), eol_reached = warning,
 * framework/version_change = neutral, ssl_fixed = success.
 *
 * "Start conversation" is the indigo commit action: it marks the signal actioned
 * and deep-links the linked prospect page (where the compose / enrol / drip
 * affordances live) — it does NOT build a new compose surface. "View prospect"
 * deep-links the same page without a state change.
 *
 * A non-Enterprise tenant sees the violet "Available on Enterprise" teaser (the
 * server 403s the data reads anyway — the FE gate just avoids the flash).
 *
 * AI Openers (pool item): the drill-down's teal "Draft an opener" turns the change
 * + prospect context into a {subject, body} via POST /compose/draft-opener. AI
 * spend is a CLICK, not a render — nothing is drafted until asked (a deliberate
 * deviation from the mockup's always-there box). "Use this draft" opens the
 * embedded compose modal prefilled; the draft is always editable and NOTHING
 * sends on its own. Quota-exhausted degrades to an honest inline top-up message.
 */
export default {
  name: "AdminTechAlerts",
  components: { MusubiKpiCard, MusubiComposeModal },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      gateLocked: null, // null=loading, true=teaser, false=entitled
      loading: false,
      busy: false,
      error: null,
      message: "",
      tab: "new",
      tabs: [
        { key: "new", label: "New", countKey: "new" },
        { key: "actioned", label: "Actioned", countKey: "actioned" },
        { key: "dismissed", label: "Dismissed", countKey: "dismissed" },
      ],
      filters: { event_type: "", search: "" },
      urgency: "", // client-side severity bucket: '' | urgent | worth | positive
      kpis: { changes_7d: 0, urgent: 0, actioned: 0, prospects_monitored: 0 },
      counts: { all: 0, new: 0, actioned: 0, dismissed: 0 },
      rows: [],
      total: 0,
      expanded: null,
      detailLoading: false,
      detailHistory: [],
      detailStack: {},
      detailProspect: null,
      // AI opener (per open drill-down; only one is expanded at a time).
      openerBusy: false,
      openerDraft: null, // null | { subject, body, to_email, to_name, subscriber_id, prospect_id }
      openerError: null,
      openerQuota: false,
      // Embedded compose modal ("Use this draft").
      composeOpen: false,
      composeContext: {},
      // Static per-event-type copy — the severity ladder + plain-English "means".
      badgeMap: {
        ssl_broken:      { cls: "danger",  label: "SSL broken" },
        site_down:       { cls: "danger",  label: "Site down" },
        eol_reached:     { cls: "warning", label: "End-of-life" },
        framework_change:{ cls: "neutral", label: "Platform change" },
        version_change:  { cls: "neutral", label: "Version change" },
        ssl_fixed:       { cls: "success", label: "SSL fixed" },
      },
      meansMap: {
        ssl_broken: "Their certificate expired — visitors now hit a browser security warning before the page even loads.",
        site_down: "Their site's returning errors — anyone Googling them right now lands on a dead page.",
        eol_reached: "Their platform no longer gets security patches — a real, growing risk you can help them off before it bites.",
        framework_change: "They've just rebuilt on a new platform — a natural moment to ask what's next, or who did it.",
        version_change: "They updated their site — someone's actively maintaining it, so they're engaged and reachable.",
        ssl_fixed: "Their certificate's been renewed — someone technical has been in recently, worth knowing who.",
      },
    };
  },
  computed: {
    dangerTypes() {
      return ["ssl_broken", "site_down"];
    },
    // Client-side severity + urgency filtering on top of the server's status/
    // event_type/search filter set.
    visibleRows() {
      return this.rows.filter((r) => {
        if (this.urgency === "urgent") return this.dangerTypes.includes(r.event_type);
        if (this.urgency === "worth") return ["eol_reached", "framework_change", "version_change"].includes(r.event_type);
        if (this.urgency === "positive") return r.event_type === "ssl_fixed";
        return true;
      });
    },
    // The urgent banner names the exact broken-site prospects still in New. Built
    // from the loaded New rows so it can never claim urgency the table can't show.
    urgentBanner() {
      if (this.tab !== "new") return { show: false, count: 0, text: "" };
      const danger = this.rows.filter((r) => r.urgent && r.status === "new");
      if (!danger.length) return { show: false, count: 0, text: "" };
      const names = danger.map((r) => r.name);
      let text;
      if (names.length === 1) {
        text = `${names[0]}'s site is broken.`;
      } else if (names.length === 2) {
        text = `${names[0]} and ${names[1]} both have broken sites right now.`;
      } else {
        text = `${names[0]}, ${names[1]} and ${names.length - 2} more have broken sites right now.`;
      }
      return { show: true, count: danger.length, text };
    },
  },
  created() {
    this.checkGate();
  },
  methods: {
    async checkGate() {
      try {
        const res = await this.api.get("/api/v1/me/usage");
        const body = res?.data ?? res;
        const entitled = !!body?.features?.tech_alerts || !!body?.plan?.can_manage;
        this.gateLocked = !entitled;
      } catch (e) {
        // Fail-open: a usage blip shouldn't lock an entitled tenant out. The
        // server-side gate stays authoritative on every data read.
        this.gateLocked = false;
      }
      if (this.gateLocked === false) this.load();
    },
    async load() {
      this.loading = true;
      this.error = null;
      this.expanded = null;
      try {
        const qs = new URLSearchParams({
          status: this.tab,
          event_type: this.filters.event_type,
          search: this.filters.search,
          per_page: "100",
        });
        const res = await this.api.get("/api/v1/admin/tech-alerts/signals?" + qs.toString());
        const body = res?.data ?? res;
        this.rows = Array.isArray(body?.rows) ? body.rows : [];
        this.counts = body?.counts || this.counts;
        this.kpis = body?.kpis || this.kpis;
        this.total = Number(body?.total) || this.rows.length;
      } catch (e) {
        this.error = this.errText(e, "Could not load tech-stack alerts.");
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    setTab(key) {
      if (this.tab === key) return;
      this.tab = key;
      this.urgency = "";
      this.load();
    },
    showUrgentOnly() {
      this.urgency = "urgent";
      this.expanded = null;
    },
    async toggleExpand(signalId) {
      if (this.expanded === signalId) {
        this.expanded = null;
        return;
      }
      this.expanded = signalId;
      this.detailLoading = true;
      this.detailHistory = [];
      this.detailStack = {};
      this.detailProspect = null;
      this.resetOpener();
      try {
        const res = await this.api.get("/api/v1/admin/tech-alerts/signals/" + signalId + "/detail");
        const body = res?.data ?? res;
        this.detailHistory = Array.isArray(body?.history) ? body.history : [];
        this.detailStack = body?.stack || {};
        this.detailProspect = body?.prospect || null;
      } catch (e) {
        this.error = this.errText(e, "Could not load the change history.");
      } finally {
        this.detailLoading = false;
      }
    },
    viewProspect(row) {
      if (!row.prospect_id) return;
      this.$router.push("/acquire/prospects/" + row.prospect_id);
    },
    // "Start conversation" — the commit action. Mark the signal actioned, then
    // deep-link the prospect page with a compose intent (?compose=1), which opens
    // the per-contact compose panel prefilled with a plain subject. AI-drafted
    // openers are a separate later slice — this is a plain prefill only.
    async startConversation(row) {
      this.busy = true;
      this.error = null;
      try {
        if (row.status === "new") {
          await this.api.post("/api/v1/admin/tech-alerts/signals/" + row.signal_id + "/action", {});
        }
        if (row.prospect_id) {
          this.$router.push("/acquire/prospects/" + row.prospect_id + "?from=tech-alert&signal=" + row.signal_id + "&compose=1");
        } else {
          this.message = "Marked actioned.";
          await this.load();
        }
      } catch (e) {
        this.error = this.errText(e, "Could not start the conversation.");
      } finally {
        this.busy = false;
      }
    },
    async dismiss(row) {
      this.busy = true;
      this.error = null;
      try {
        await this.api.post("/api/v1/admin/tech-alerts/signals/" + row.signal_id + "/dismiss", {});
        this.message = "Signal dismissed.";
        this.expanded = null;
        await this.load();
      } catch (e) {
        this.error = this.errText(e, "Could not dismiss.");
      } finally {
        this.busy = false;
      }
    },
    // ── AI opener (pool item) ──
    resetOpener() {
      this.openerBusy = false;
      this.openerDraft = null;
      this.openerError = null;
      this.openerQuota = false;
    },
    // Draft an opener for THIS signal's linked prospect. Grounds on the change
    // (signal_id) + prospect/company context server-side. Never sends.
    async draftOpener(row) {
      if (this.openerBusy || !this.detailProspect) return;
      this.openerBusy = true;
      this.openerError = null;
      this.openerQuota = false;
      try {
        const payload = { prospect_id: this.detailProspect.prospect_id, signal_id: row.signal_id };
        const res = await this.api.post("/api/v1/admin/compose/draft-opener", payload);
        const body = res?.data ?? res;
        if (body?.success) {
          this.openerDraft = {
            subject: body.subject || "",
            body: body.body || "",
            to_email: body.to_email || "",
            to_name: body.to_name || "",
            subscriber_id: Number(body.subscriber_id || 0),
            prospect_id: Number(body.prospect_id || this.detailProspect.prospect_id || 0),
          };
        } else if (body?.quota_exhausted) {
          this.openerQuota = true;
        } else {
          this.openerError = body?.error || "Could not draft an opener just now.";
        }
      } catch (e) {
        this.openerError = this.errText(e, "Could not draft an opener just now.");
      } finally {
        this.openerBusy = false;
      }
    },
    // "Use this draft" — open the embedded compose modal prefilled with the (still
    // editable) subject + body. The human reviews, edits and sends from there.
    useDraft() {
      if (!this.openerDraft) return;
      const d = this.openerDraft;
      this.composeContext = {
        to_email: d.to_email,
        to_name: d.to_name,
        subject: d.subject,
        body: d.body,
        prospect_id: d.prospect_id,
        subscriber_id: d.subscriber_id,
        signal_id: this.expanded || 0,
      };
      this.composeOpen = true;
    },
    onComposed() {
      this.composeOpen = false;
      this.message = "Queued — it'll send on the next tick.";
    },
    // ── severity ladder + change copy ──
    badgeClass(t) { return (this.badgeMap[t] || { cls: "neutral" }).cls; },
    badgeLabel(t) { return (this.badgeMap[t] || { label: t }).label; },
    meansText(t) { return this.meansMap[t] || ""; },
    newTone(t) {
      if (this.dangerTypes.includes(t) || t === "eol_reached") return "bad";
      if (t === "ssl_fixed") return "good";
      return "";
    },
    oldLabel(r) {
      if (r.event_type === "ssl_broken") return "SSL valid";
      if (r.event_type === "ssl_fixed") return "Certificate expired";
      if (r.event_type === "site_down") return "Online";
      return this.pretty(r.old_value);
    },
    newLabel(r) {
      if (r.event_type === "ssl_broken") return "Certificate expired";
      if (r.event_type === "ssl_fixed") return "SSL valid";
      if (r.event_type === "site_down") return "Not responding";
      if (r.event_type === "eol_reached") return (r.old_value ? this.pretty(r.old_value) + " " : "") + "reached end-of-life";
      return this.pretty(r.new_value);
    },
    // ── drill-down history copy ──
    historyTitle(h) {
      const b = this.badgeMap[h.event_type];
      if (h.event_type === "ssl_broken") return "SSL certificate expired";
      if (h.event_type === "ssl_fixed") return "SSL certificate renewed";
      if (h.event_type === "site_down") return "Site went down";
      if (h.event_type === "eol_reached") return (h.old_value ? this.pretty(h.old_value) + " " : "") + "reached end-of-life";
      if (h.event_type === "framework_change") return "Platform changed" + (h.new_value ? " to " + this.pretty(h.new_value) : "");
      if (h.event_type === "version_change") return "Version updated" + (h.old_value && h.new_value ? " " + this.pretty(h.old_value) + " → " + this.pretty(h.new_value) : "");
      return b ? b.label : h.event_type;
    },
    historySub(h) {
      const bits = [h.field || h.event_type];
      if (h.old_value || h.new_value) bits.push(this.pretty(h.old_value) + " → " + this.pretty(h.new_value));
      return bits.join(" · ");
    },
    // ── stack snapshot chips ──
    sslChipClass(s) { return s === "expired" || s === "broken" || s === "invalid" ? "bad" : (s === "valid" ? "ok" : ""); },
    sslLabel(s) { return s ? this.pretty(s) : "Unknown"; },
    reachChipClass(s) { return s === "unreachable" || s === "down" ? "bad" : (s === "active" ? "ok" : ""); },
    reachLabel(s) { return s === "unreachable" || s === "down" ? "Site down" : (s === "active" ? "Site online" : (s ? this.pretty(s) : "Unknown")); },
    eolLabel(s) { return s === "eol" ? "End-of-life" : this.pretty(s); },
    // ── prospect card ──
    prettyStage(s) { return this.pretty(s) || "—"; },
    tempClass(t) { return t === "warm" ? "warm" : (t === "hot" ? "hot" : "cold"); },
    prettyTemp(t) { return t ? this.pretty(t) : "Cold"; },
    // ── formatting helpers ──
    pretty(s) {
      if (s === null || s === undefined || s === "") return "";
      return String(s).replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    prettyHost(url) {
      return String(url || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
    },
    isFresh(row) {
      if (!row.occurred_at) return false;
      return (Date.now() / 1000 - Number(row.occurred_at)) / 86400 <= 2;
    },
    ageLabel(epoch) {
      epoch = Number(epoch);
      if (!epoch) return "—";
      const s = Math.max(0, Math.floor(Date.now() / 1000) - epoch);
      if (s < 3600) return Math.max(1, Math.floor(s / 60)) + "m ago";
      if (s < 86400) return Math.floor(s / 3600) + "h ago";
      const days = Math.floor(s / 86400);
      if (days === 1) return "Yesterday";
      if (days < 30) return days + " days ago";
      return this.fmtDate(epoch);
    },
    fmtDate(epoch) {
      if (!epoch) return "—";
      return new Date(Number(epoch) * 1000).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
    },
    fmtDateTime(epoch) {
      if (!epoch) return "—";
      const d = new Date(Number(epoch) * 1000);
      return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) + ", " +
        d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    relTime(epoch) {
      epoch = Number(epoch);
      if (!epoch) return "—";
      const s = Math.max(0, Math.floor(Date.now() / 1000) - epoch);
      if (s < 3600) return Math.max(1, Math.floor(s / 60)) + " minutes ago";
      if (s < 86400) return Math.floor(s / 3600) + " hours ago";
      if (s < 172800) return "Yesterday";
      if (s < 2592000) return Math.floor(s / 86400) + " days ago";
      return this.fmtDate(epoch);
    },
    errText(e, fallback) {
      const body = e?.response?.data ?? e?.data;
      if (body?.error === "plan_gated") return body.message || fallback;
      return body?.message || body?.error || fallback;
    },
  },
};
</script>

<style scoped>
.musubi-page--techalerts { max-width: 1240px; }
.musubi-page__head h1 { font-size: 1.6rem; font-weight: 700; margin: 0 0 6px; display: flex; align-items: center; gap: 10px; }
.musubi-page__head p { margin: 0; max-width: 760px; line-height: 1.55; font-size: 0.92rem; }
.text-muted { color: var(--m-text-secondary); }
.plan-pill { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.05em; text-transform: uppercase; color: var(--m-accent-hover, #4338CA); background: var(--m-accent-soft, #EEF0FF); border: 1px solid #D9DCF7; border-radius: 999px; padding: 3px 9px; position: relative; top: -2px; }

.coverage-note { display: flex; align-items: flex-start; gap: 9px; margin: 16px 0 8px; font-size: 0.8rem; color: var(--m-text-secondary); line-height: 1.5; }
.coverage-note i { color: var(--m-status-info); margin-top: 2px; }

.ta-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin: 8px 0; }
@media (max-width: 1024px) { .ta-kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px) { .ta-kpis { grid-template-columns: 1fr; } }

/* Inline urgent KPI tile (danger variant the shared card lacks) */
.m-kpi { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); padding: var(--m-space-3, 12px) var(--m-space-4, 16px); box-shadow: var(--m-shadow-card); }
.m-kpi.urgent { border-color: #F6CBD6; background: linear-gradient(180deg, #FFF7F9, var(--m-bg-card)); }
.m-kpi__label { display: flex; align-items: center; gap: 7px; color: var(--m-text-secondary); font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; }
.m-kpi__icon { color: var(--m-accent); }
.m-kpi__icon.danger { color: var(--m-status-danger, #E11D48); }
.m-kpi__value { font-size: 1.75rem; font-weight: 750; margin-top: 6px; color: var(--m-text-primary); }
.m-kpi__value.danger { color: var(--m-status-danger, #E11D48); }
.m-kpi__delta { font-size: 0.78rem; margin-top: 3px; color: var(--m-text-tertiary); }
.m-kpi__delta.danger { color: #B4123E; font-weight: 600; }

/* Urgent-attention banner */
.urgent-banner { display: flex; align-items: flex-start; gap: 13px; padding: 15px 18px; border-radius: 12px; background: linear-gradient(180deg, #FEF2F5, #FFF8FA); border: 1px solid #F6CBD6; margin: 16px 0 4px; }
.urgent-banner__ic { width: 38px; height: 38px; border-radius: 10px; background: #FDE3E9; color: var(--m-status-danger, #E11D48); display: flex; align-items: center; justify-content: center; flex: none; }
.urgent-banner__body { flex: 1; min-width: 0; }
.urgent-banner__t { font-weight: 700; font-size: 0.92rem; color: #9F1239; }
.urgent-banner__d { font-size: 0.82rem; color: #8A2540; line-height: 1.5; margin-top: 3px; }
.urgent-banner__cta { flex: none; align-self: center; }

.ta-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--m-border); margin: 22px 0 0; }
.ta-tab { padding: 9px 16px; font-size: 0.86rem; font-weight: 500; color: var(--m-text-secondary); border: none; background: none; border-bottom: 2px solid transparent; cursor: pointer; }
.ta-tab.active { color: var(--m-accent); border-bottom-color: var(--m-accent); font-weight: 600; }
.ta-tab .count { font-size: 0.7rem; background: var(--m-accent-soft); color: var(--m-accent-hover); padding: 1px 7px; border-radius: 999px; margin-left: 6px; font-weight: 700; }

.ta-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin: 16px 0; }
.ta-select, .ta-input { border: 1px solid var(--m-border); background: var(--m-bg-card); border-radius: 6px; font-size: 0.82rem; padding: 6px 10px; color: var(--m-text-primary); font-family: inherit; }

.btn { font-family: inherit; font-weight: 500; font-size: 0.82rem; padding: 0.4rem 0.85rem; border-radius: 6px; border: 1px solid transparent; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; text-decoration: none; line-height: 1.3; white-space: nowrap; }
.btn:disabled { opacity: 0.5; cursor: default; }
.btn-sm { font-size: 0.76rem; padding: 0.3rem 0.6rem; }
.ta-btn-primary { background: var(--m-accent); border-color: var(--m-accent); color: #fff; }
.ta-btn-primary:hover:not(:disabled) { background: var(--m-accent-hover); border-color: var(--m-accent-hover); }
.ta-btn-outline { color: var(--m-accent-hover); border-color: var(--m-accent); background: var(--m-bg-card); }
.ta-btn-outline:hover:not(:disabled) { background: var(--m-accent); color: #fff; }
.ta-btn-secondary { color: var(--m-text-secondary); border-color: var(--m-border); background: var(--m-bg-card); }
.ta-btn-secondary:hover:not(:disabled) { background: var(--m-accent-soft); border-color: var(--m-accent); color: var(--m-text-primary); }
.ta-btn-danger { background: var(--m-status-danger, #E11D48); border-color: var(--m-status-danger, #E11D48); color: #fff; }
.ta-btn-danger:hover:not(:disabled) { background: #C60E3D; border-color: #C60E3D; }
.btn-ghost { background: none; border: none; color: var(--m-text-secondary); padding: 6px 8px; cursor: pointer; border-radius: 6px; }
.btn-ghost:hover:not(:disabled) { background: var(--m-rail-hover-bg); color: var(--m-text-primary); }
.ta-dismissed { display: inline-flex; align-items: center; gap: 5px; font-size: 0.78rem; font-weight: 600; color: var(--m-text-tertiary); }

.ta-loading { padding: 32px; text-align: center; color: var(--m-text-secondary); }

.ta-tbl { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); overflow: hidden; }
.ta-tbl table { width: 100%; border-collapse: collapse; }
.ta-tbl thead th { background: rgba(79, 70, 229, 0.07); border-bottom: 1px solid var(--m-border); text-align: left; font-size: 0.66rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: var(--m-text-secondary); padding: 11px 14px; white-space: nowrap; }
.ta-tbl tbody td { padding: 0.7rem 0.85rem; font-size: 0.85rem; border-bottom: 1px solid var(--m-border); vertical-align: top; }
.ta-tbl tbody tr:last-child td { border-bottom: none; }
.ta-tbl tbody tr.is-open { background: rgba(225, 29, 72, 0.035); }
.ta-tbl tbody tr.danger-row td:first-child { box-shadow: inset 3px 0 0 var(--m-status-danger, #E11D48); }
.co-name { font-weight: 600; color: var(--m-text-primary); }
.co-web { font-size: 0.76rem; color: var(--m-text-secondary); display: flex; align-items: center; gap: 5px; margin-top: 2px; }
.co-web i { font-size: 0.85em; color: var(--m-text-tertiary); }
.chev { background: none; border: none; color: var(--m-text-tertiary); cursor: pointer; padding: 2px; display: inline-flex; transition: transform 0.15s; margin-top: 1px; }
.chev.open { transform: rotate(90deg); color: var(--m-status-danger, #E11D48); }

/* Severity ladder badge */
.alert-badge { display: inline-flex; align-items: center; gap: 6px; padding: 0.18rem 0.55rem; font-size: 0.71rem; font-weight: 700; border-radius: 5px; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.03em; }
.alert-badge .dot { width: 6px; height: 6px; border-radius: 50%; flex: none; }
.alert-badge.danger { background: #FEECF0; color: #B4123E; border: 1px solid #F6CBD6; }
.alert-badge.danger .dot { background: var(--m-status-danger, #E11D48); }
.alert-badge.warning { background: #FFF4E6; color: #9A5B12; border: 1px solid #F0DCBB; }
.alert-badge.warning .dot { background: var(--m-status-warning, #D97706); }
.alert-badge.neutral { background: #EEF2F8; color: #41526B; border: 1px solid #DCE3EE; }
.alert-badge.neutral .dot { background: #5B6B85; }
.alert-badge.success { background: #E1F3EF; color: var(--m-teal-text, #0F766E); border: 1px solid #BBE7DF; }
.alert-badge.success .dot { background: var(--m-status-success, #0D9488); }

.change { display: inline-flex; align-items: center; gap: 8px; font-size: 0.82rem; flex-wrap: wrap; }
.change .old { color: var(--m-text-secondary); text-decoration: line-through; text-decoration-color: #C6CDDA; }
.change .ar { color: var(--m-text-tertiary); }
.change .new { font-weight: 600; color: var(--m-text-primary); }
.change .new.bad { color: #B4123E; }
.change .new.good { color: var(--m-teal-text, #0F766E); }
.means { display: flex; align-items: flex-start; gap: 6px; font-size: 0.77rem; color: var(--m-text-secondary); line-height: 1.4; max-width: 320px; margin-top: 6px; }
.means i { flex: none; margin-top: 2px; color: var(--m-text-tertiary); }

.age { font-size: 0.82rem; display: inline-flex; align-items: center; gap: 6px; color: var(--m-text-secondary); white-space: nowrap; }
.age i { color: var(--m-text-tertiary); }
.age.fresh { color: #B4123E; font-weight: 600; }
.age.fresh i { color: var(--m-status-danger, #E11D48); }
.row-actions { display: flex; align-items: center; gap: 6px; justify-content: flex-end; flex-wrap: wrap; }

/* Drill-down */
.drill { background: #FBFCFE; }
.drill__loading { padding: 22px 26px; color: var(--m-text-secondary); font-size: 0.85rem; }
.drill__inner { display: grid; grid-template-columns: 1.15fr 1fr; gap: 26px; padding: 22px 26px; }
@media (max-width: 900px) { .drill__inner { grid-template-columns: 1fr; } }
.drill h4 { margin: 0 0 12px; font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--m-text-secondary); }
.drill h4 + h4 { margin-top: 20px; }
.timeline2 { position: relative; padding-left: 20px; margin-bottom: 6px; }
.timeline2::before { content: ""; position: absolute; left: 5px; top: 5px; bottom: 5px; width: 2px; background: var(--m-border-strong); }
.tl2 { position: relative; padding: 0 0 14px; }
.tl2::before { content: ""; position: absolute; left: -18px; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: var(--m-bg-card); border: 2px solid var(--m-text-tertiary); }
.tl2.now::before { background: var(--m-status-danger, #E11D48); border-color: var(--m-status-danger, #E11D48); }
.tl2__t { font-weight: 600; font-size: 0.84rem; }
.tl2__t .mini { font-weight: 700; font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.03em; color: #B4123E; background: #FEECF0; border: 1px solid #F6CBD6; border-radius: 4px; padding: 0 5px; margin-left: 6px; }
.tl2__m { font-size: 0.74rem; color: var(--m-text-secondary); margin-top: 1px; }
.stack { display: flex; gap: 8px; flex-wrap: wrap; }
.stack-chip { display: inline-flex; flex-direction: column; gap: 1px; font-size: 0.8rem; font-weight: 600; padding: 0.35rem 0.6rem; border-radius: 8px; background: #F4F6FA; border: 1px solid var(--m-border); color: var(--m-text-primary); }
.stack-chip .k { font-size: 0.58rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-text-tertiary); font-weight: 700; }
.stack-chip.bad { background: #FEECF0; border-color: #F6CBD6; color: #B4123E; }
.stack-chip.bad .k { color: #C4506A; }
.stack-chip.warn { background: #FFF4E6; border-color: #F0DCBB; color: #9A5B12; }
.stack-chip.warn .k { color: #B08331; }
.stack-chip.ok { background: #E1F3EF; border-color: #BBE7DF; color: var(--m-teal-text, #0F766E); }
.stack-chip.ok .k { color: #3E9187; }

.prospect { background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: 12px; padding: 14px 16px; margin-bottom: 16px; }
.prospect__top { display: flex; align-items: center; gap: 10px; }
.prospect__name { font-weight: 600; font-size: 0.9rem; }
.prospect__link { margin-left: auto; font-size: 0.78rem; font-weight: 600; color: var(--m-accent-hover); text-decoration: none; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap; }
.prospect__link:hover { text-decoration: underline; }
.prospect__meta { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 11px; }
.stage-chip { display: inline-flex; align-items: center; gap: 6px; font-size: 0.72rem; font-weight: 600; padding: 0.16rem 0.55rem; border-radius: 999px; background: var(--m-accent-soft); color: var(--m-accent-hover); }
.temp { display: inline-flex; align-items: center; gap: 5px; font-size: 0.7rem; font-weight: 700; padding: 0.16rem 0.55rem; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.03em; }
.temp .dot { width: 6px; height: 6px; border-radius: 50%; }
.temp.warm { background: #FFF4E6; color: #9A5B12; }
.temp.warm .dot { background: var(--m-status-warning, #D97706); }
.temp.hot { background: #FEECF0; color: #B4123E; }
.temp.hot .dot { background: var(--m-status-danger, #E11D48); }
.temp.cold { background: #EEF2F8; color: #41526B; }
.temp.cold .dot { background: #5B6B85; }
.prospect__last { font-size: 0.77rem; color: var(--m-text-secondary); margin-top: 11px; display: flex; align-items: center; gap: 6px; }
.prospect__last i { color: var(--m-text-tertiary); }
.drill__cta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-top: 4px; }

/* AI-drafted opener box (teal AI canon, ported from the approved mockup) */
.ai-opener-wrap { margin: 16px 0 4px; }
.ta-ai-trigger { color: var(--m-teal-text, #0F766E); background: var(--m-teal-soft, #E4F7F4); border: 1px solid var(--m-teal-line, #B6EBE4); }
.ta-ai-trigger:hover:not(:disabled) { background: var(--m-teal, #14B8A6); border-color: var(--m-teal, #14B8A6); color: #fff; }
.ai-opener-err { display: flex; align-items: flex-start; gap: 7px; margin-top: 10px; font-size: 0.78rem; color: #9A5B12; line-height: 1.45; }
.ai-opener-quota { display: flex; align-items: flex-start; gap: 7px; margin-top: 10px; padding: 10px 12px; background: #FFF7ED; border: 1px solid #FED7AA; border-radius: 9px; font-size: 0.78rem; color: #9A5B12; line-height: 1.45; }
.ai-opener-quota a { color: var(--m-teal-text, #0F766E); font-weight: 700; }
.ai-opener { background: linear-gradient(135deg, var(--m-teal-soft, #E4F7F4), #F4FCFB); border: 1px solid var(--m-teal-line, #B6EBE4); border-radius: 12px; padding: 15px 16px; }
.ai-opener__head { display: flex; align-items: center; gap: 8px; font-size: 0.72rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; color: var(--m-teal-text, #0F766E); }
.ai-opener__head i { color: var(--m-teal, #14B8A6); }
.ai-opener__badge { margin-left: auto; font-size: 0.58rem; font-weight: 800; letter-spacing: 0.04em; color: var(--m-teal-text, #0F766E); background: #fff; border: 1px solid var(--m-teal-line, #B6EBE4); border-radius: 999px; padding: 2px 8px; }
.ai-opener__body { font-size: 0.85rem; line-height: 1.55; margin-top: 10px; color: var(--m-text-primary); }
.ai-opener__quote { margin-top: 10px; padding: 11px 13px; background: #fff; border: 1px solid var(--m-teal-line, #B6EBE4); border-radius: 9px; font-size: 0.85rem; line-height: 1.55; color: var(--m-text-primary); }
.ai-opener__subject { font-weight: 700; margin-bottom: 6px; }
.ai-opener__text { font-style: italic; white-space: pre-wrap; }
.ai-opener__note { display: flex; gap: 7px; align-items: flex-start; margin-top: 11px; font-size: 0.74rem; color: var(--m-text-secondary); line-height: 1.45; }
.ai-opener__note i { flex: none; margin-top: 1px; color: var(--m-teal-text, #0F766E); }
.ai-opener__cta { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; margin-top: 13px; }

/* Empty state */
.dc-empty { text-align: center; padding: 48px 24px; color: var(--m-text-secondary); background: var(--m-bg-card); border: 1px solid var(--m-border); border-radius: var(--m-radius-lg, 14px); margin-top: 16px; }
.dc-empty.teal-edge { border-top: 3px solid var(--m-teal, #14B8A6); }
.dc-empty__icon { display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px; border-radius: 50%; background: var(--m-teal-soft, #E4F7F4); color: var(--m-teal-text, #0F766E); font-size: 24px; margin-bottom: 16px; }
.dc-empty__title { font-weight: 600; color: var(--m-text-primary); font-size: 1rem; margin-bottom: 8px; }
.dc-empty__hint { font-size: 0.86rem; max-width: 560px; margin: 0 auto 8px; line-height: 1.6; }
.dc-empty__why { max-width: 560px; margin: 14px auto 0; text-align: left; display: grid; gap: 8px; }
.dc-empty__why .row { display: flex; gap: 9px; align-items: flex-start; font-size: 0.82rem; line-height: 1.45; }
.dc-empty__why .row i { color: var(--m-status-info); margin-top: 3px; }
.dc-empty__actions { margin-top: 22px; display: flex; gap: 10px; justify-content: center; }

/* Violet Enterprise teaser */
.ta-teaser { position: relative; border-radius: var(--m-radius-lg, 14px); overflow: hidden; border: 1px solid var(--m-border); margin: 20px 0; min-height: 340px; }
.ta-teaser__blur { filter: blur(4px); opacity: 0.55; pointer-events: none; user-select: none; padding: 8px; }
.ta-skel-tbl { background: var(--m-bg-card); }
.ta-skel-row { display: flex; gap: 18px; align-items: center; padding: 16px 18px; border-bottom: 1px solid var(--m-border); }
.skel { height: 11px; border-radius: 5px; background: linear-gradient(90deg, #E9ECF3, #F3F5F9, #E9ECF3); display: inline-block; }
.ta-teaser__panel { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: linear-gradient(135deg, rgba(243, 238, 254, 0.72), rgba(251, 250, 255, 0.82)); }
.upgrade { max-width: 540px; text-align: center; background: linear-gradient(135deg, var(--m-violet-soft, #F3EEFE), #FBFAFF); border: 1px solid var(--m-violet-line, #E0D2FB); border-radius: 16px; padding: 30px 34px; box-shadow: var(--m-shadow-panel); }
.upgrade__ic { width: 52px; height: 52px; border-radius: 14px; background: var(--m-bg-card); border: 1px solid var(--m-violet-line, #E0D2FB); color: var(--m-violet, #7C3AED); display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin: 0 auto 14px; }
.upgrade__eyebrow { font-size: 0.68rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: var(--m-violet, #7C3AED); margin-bottom: 8px; }
.upgrade h3 { margin: 0 0 8px; font-size: 1.25rem; }
.upgrade p { margin: 0 0 18px; font-size: 0.88rem; color: var(--m-text-secondary); line-height: 1.55; }
.upgrade__benefits { text-align: left; display: grid; gap: 10px; margin: 0 auto 22px; max-width: 410px; }
.benefit { display: flex; gap: 10px; align-items: flex-start; font-size: 0.86rem; line-height: 1.4; }
.benefit i { color: var(--m-violet, #7C3AED); margin-top: 3px; }
.upgrade__actions { display: flex; gap: 10px; justify-content: center; align-items: center; }
.ta-btn-violet { background: var(--m-violet, #7C3AED); border-color: var(--m-violet, #7C3AED); color: #fff; }
.ta-btn-violet:hover { background: #6D28D9; }
.ta-compare { font-size: 0.85rem; font-weight: 700; color: var(--m-violet, #7C3AED); text-decoration: none; display: inline-flex; align-items: center; gap: 5px; }
.ta-compare:hover { text-decoration: underline; }
</style>
